import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { getLibreFAQParam, sendGtEvent } from 'analytics/analytics';

import { GT_LIBRE_LANDING_PAGE_SHOWN, GT_LP_FAQ_CLICKED, GT_LP_RECEIVE_MONEY_CLICKED } from 'constants/GoogleAnalyticsEvents';
import { GT_RECEIVING_COUNTRY, GT_SECTION_CLICKED, GT_TRANSACTION_ID } from 'constants/GoogleAnalyticsParamNames';

import { resolveArguments } from 'helpers/utils';

import { useMatchMedia } from 'hooks/useMatchMedia';

import { LibreButton, LibreFloatingButton, LibreHeroBlock } from './styles';
import promoBreakpoints from '../../../theme/promoBreakpoints';
import ColoredSectionContainer from '../common/ColoredSectionContainer';
import { LightDiskBadge } from '../common/fromAuth/LightDiskBadge/LightDiskBadge';
import { PromoButton } from '../common/PromoButton/PromoButton';
import { PromoDangerouslyHtml } from '../common/PromoDangerouslyHtml/PromoDangerouslyHtml';
import { PromoRow } from '../common/PromoRow/PromoRow';
import { PromoSectionContainer } from '../common/PromoSectionContainer';
import { PromoText } from '../common/PromoText/PromoText';
import FaqSection from '../common/sections/FaqSection';
import HeadingSection from '../common/sections/HeadingSection';
import { TilesSection } from '../common/sections/TilesSection';
import TrustpilotSection from '../common/sections/TrustpilotSection';
import NotAuthBasePage from '../NotAuthBasePage';

const LibreNoAuthInfo = (props) => {
    const toCurCode = props.paymentInfo?.toCurCode;
    const amount = props.paymentInfo?.toAmtReal;
    const comment = props.paymentInfo?.payComment;
    const fromTitle = resolveArguments(props.fromTitleWithoutName, props.paymentInfo.extra.sender_first_name + ' ' + props.paymentInfo.extra.sender_last_name);

    const avatar = props.paymentInfo?.mobileInfo?.iconLogoAvatar;

    const isDesktop = useMatchMedia(promoBreakpoints.desktop1440);
    const [fixedButton, setFixedButton] = useState(false);

    useEffect(() => {
        sendEvent(GT_LIBRE_LANDING_PAGE_SHOWN);
        const handleScroll = () => {
            const floatingButton = document.getElementById('floating-button');
            const buttonOffsetTop = floatingButton.getBoundingClientRect().top + window.scrollY + floatingButton.offsetHeight;

            if (window.scrollY >= buttonOffsetTop) {
                setFixedButton(true);
            } else {
                setFixedButton(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const sendEvent = (eventName, questionId) => {
        const paymentInfo = props.paymentInfo;
        const params = {
            [GT_TRANSACTION_ID]: paymentInfo?.payId,
            [GT_RECEIVING_COUNTRY]: paymentInfo?.extra?.recipient_country,
        };
        if (questionId) {
            params[GT_SECTION_CLICKED] = getLibreFAQParam(questionId);
        }
        sendGtEvent(eventName, params);
    };

    const sendFaqClickedEvent = (questionId) => {
        sendEvent(GT_LP_FAQ_CLICKED, questionId);
    };

    return (
        <>
            <LibreHeroBlock>
                <PromoSectionContainer>
                    <PromoRow textAlign={'center'} indent={'32px'}>
                        <LightDiskBadge src={avatar ? avatar : null} iconName={!avatar ? 'person-48' : null} flagName={'paysend'} diskSize={'x-large'} />
                    </PromoRow>
                    <PromoRow indent={'32px'}>
                        <PromoRow indent={'4px'}>
                            <PromoText tag={'div'} variant={'subheader'} color={'white'} textAlign={'center'}>
                                {fromTitle}
                            </PromoText>
                        </PromoRow>
                        <PromoRow indent={'8px'}>
                            <PromoText tag={'div'} variant={'h1'} color={'white'} textAlign={'center'}>
                                {amount + ' '}
                                <PromoText tag={'span'} variant={'h1'} color={'white'} smallCaps>
                                    {toCurCode.toLowerCase()}
                                </PromoText>
                            </PromoText>
                        </PromoRow>
                        {comment && (
                            <PromoText tag={'div'} variant={'subheader'} color={'whiteRgba48'} textAlign={'center'}>
                                {comment}
                            </PromoText>
                        )}
                    </PromoRow>
                    <PromoRow indent={'56px'}>
                        <PromoDangerouslyHtml tag={'div'} variant={'subheader'} color={'white'} textAlign={'center'} dangerouslyHtml={props.promoText} />
                    </PromoRow>
                    <LibreButton id={'floating-button'} showFixedButton={fixedButton}>
                        <PromoButton
                            type={'link'}
                            caption={props.ctaButtonText}
                            href={props.continueLink}
                            variant={'primary'}
                            isInverted
                            onClick={() => sendEvent(GT_LP_RECEIVE_MONEY_CLICKED)}
                        />
                    </LibreButton>
                </PromoSectionContainer>
            </LibreHeroBlock>
            <ColoredSectionContainer hasBorderRadius bgColor={'white'}>
                {props.lang && <TrustpilotSection lang={props.lang} />}
                {props.headline1Title && <HeadingSection title={props.headline1Title} />}
                <TilesSection data={props.tiles} grayText />
                {props.headline2Title && <HeadingSection title={props.headline2Title} />}
                <TilesSection data={props.tiles2} grayText />
            </ColoredSectionContainer>
            {props.faqTitle && props.questions && <FaqSection title={props.faqTitle} questions={props.questions} sendEvent={sendFaqClickedEvent} />}
            {fixedButton && !isDesktop && (
                <LibreFloatingButton>
                    <PromoSectionContainer>
                        <PromoButton
                            type={'link'}
                            caption={props.ctaButtonText}
                            href={props.continueLink}
                            variant={'primary'}
                            onClick={() => sendEvent(GT_LP_RECEIVE_MONEY_CLICKED)}
                        />
                    </PromoSectionContainer>
                </LibreFloatingButton>
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        fromTitleWithoutName: state.extra?.strapiApi?.fromTitle,
        promoText: state.extra?.strapiApi?.promoText,

        ctaButtonText: state.extra?.strapiApi?.ctaButtonText,
        continueLink: state.extra?.strapiApi?.continueLink,

        headline1Title: state.extra?.strapiApi?.headline1?.title,
        tiles: state.extra?.strapiApi?.tiles_section,
        headline2Title: state.extra?.strapiApi?.headline2?.title,
        tiles2: state.extra?.strapiApi?.tiles_section2,
        faqTitle: state.extra?.strapiApi?.faq?.title,
        questions: state.extra?.strapiApi?.faq?.questions,

        lang: state.extra.noAuthFooter?.language?.lang,
    };
};
export default connect(mapStateToProps)(NotAuthBasePage(LibreNoAuthInfo));
